import { useState, useEffect, useRef } from "react";

export function useScrollSpy(sectionIds: string[], offset: number = 100) {
  const [activeSection, setActiveSection] = useState<string>("");
  const rafRef = useRef<number>();
  const sectionsCache = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Cache section positions on mount and resize
    const cacheSectionPositions = () => {
      sectionsCache.current.clear();
      sectionIds.forEach((id) => {
        const section = document.getElementById(id);
        if (section) {
          sectionsCache.current.set(id, section.offsetTop);
        }
      });
    };

    cacheSectionPositions();
    window.addEventListener("resize", cacheSectionPositions, { passive: true });

    const handleScroll = () => {
      // Cancel any pending RAF
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use RAF to batch DOM reads
      rafRef.current = requestAnimationFrame(() => {
        const scrollPosition = window.scrollY + offset;

        for (let i = sectionIds.length - 1; i >= 0; i--) {
          const cachedTop = sectionsCache.current.get(sectionIds[i]);
          if (cachedTop !== undefined && cachedTop <= scrollPosition) {
            setActiveSection(sectionIds[i]);
            return;
          }
        }
        setActiveSection(sectionIds[0] || "");
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", cacheSectionPositions);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [sectionIds, offset]);

  return activeSection;
}
